import {
    JsonHubProtocol,
    HttpTransportType,
    HubConnectionBuilder,
    LogLevel,
    HubConnectionState
} from '@microsoft/signalr';

import {
    getUrlMessageApiWebSocket,
    showMessageToastCustom,
    translate,
    isNullOrEmpty,
    sendPushNotification,
    sendPushNotificationShipmentTracking
} from '../utils/util';
import PickingService from '../services/PickingService';
import LinePickingService from '../services/LinePickingService';
import InventoryService from '../services/InventoryService';
import { ENUMS } from '../utils/constants';

let hub;
let vision;

const signalRMiddleware = (store) => (next) => (action) => {
    // eslint-disable-next-line no-return-await
    const startSignalRConnection = async () => {
        vision = action.user.vision;
        try {
            await hub.start();
            console.assert(hub.state === HubConnectionState.Connected);
            console.log(":::no-loc(SignalR)::: Logistics Message: Connected.");
        } catch (err) {
            console.assert(hub.state === HubConnectionState.Disconnected);
            console.log(err);
            setTimeout(() => startSignalRConnection(), 5000);
        }
    };

    /*=======================================================================================
    *   Common
    =======================================================================================*/
    const onMessageResponseReceived = (msg) => {
        // console.info('****** NOTIFICATION ******', msg);
        showMessageToastCustom(translate('warning', 'general'), msg, 8000, 'mdi mdi-message-bulleted blue-text');
    };

    const onPushNotification = (notification) => {
        // eslint-disable-next-line no-console
        console.info('****** PUSH NOTIFICATION NOTIFICATION ******', notification);
        sendPushNotification(notification);
    };

    /*=======================================================================================
    *   Expedition Picking
    =======================================================================================*/
    const onPickingResponseReceived = (orderItem) => {
        // console.info('****** NOTIFICATION PICKING ******', orderItem);
        store.dispatch(PickingService.confirmPickingFromServer(orderItem));
    };

    const onRePickingResponseReceived = (orderItem) => {
        // console.info('****** NOTIFICATION REPICKING ******', orderItem);
        store.dispatch(PickingService.confirmRepickingFromServer(orderItem));
    };

    const onPickingRefreshOrderResponseReceived = (order) => {
        // console.info('****** NOTIFICATION REFRESH ORDER ******', order);
        if (order.type === ENUMS.eOrderType.LinePicking.value) {
            store.dispatch(LinePickingService.updateOrderFromServer(order));
        }
        else {
            store.dispatch(PickingService.updateOrderFromServer(order));
        }
    };

    const onPickingRefreshOrderStatusResponseReceived = (orderId, status) => {
        // console.info('****** NOTIFICATION CHANGE STATUS ORDER ******', order);
        store.dispatch(PickingService.changeStatusOrder(orderId, status));
    };

    const onPickingLockOrderResponseReceived = (orderId, currentUser) => {
        // eslint-disable-next-line no-console
        console.info('****** NOTIFICATION EXPEDITION PICKING LOCK ORDER ******', orderId, currentUser);
    };

    const onPickingUnlockOrderResponseReceived = (orderId) => {
        // eslint-disable-next-line no-console
        console.info('****** NOTIFICATION EXPEDITION PICKING UNLOCK ORDER ******', orderId);
    };

    const onLinePickingCancelOrder = (orderId) => {
        // eslint-disable-next-line no-console
        store.dispatch(LinePickingService.cancelOrderFromServer(orderId));
        console.info('****** NOTIFICATION EXPEDITION LINE PICKING CANCEL ORDER ******', orderId);
    };

    const onLinePickingRefreshOrderResponseReceived = (order, changeUser) => {
        console.info('****** NOTIFICATION REFRESH ORDER ******', order);
        if (order.type === ENUMS.eOrderType.LinePicking.value) {
            store.dispatch(LinePickingService.updateOrderFromServer(order, changeUser));
        }
        else {
            store.dispatch(PickingService.updateOrderFromServer(order));
        }
    };

    const onLinePickingAddOrder = (order) => {
        // eslint-disable-next-line no-console
        store.dispatch(LinePickingService.addOrderFromServer(order));
        console.info('****** NOTIFICATION EXPEDITION LINE PICKING ADD ORDER ******', order);
    };

    /*=======================================================================================
    *   Inventory
    =======================================================================================*/

    const onInventoryRefreshStatus = (inventoryId, status) => {
        store.dispatch(InventoryService.refreshGeneralInventoryStatus(inventoryId, status));
    };

    const onInventoryRefreshLot = (lot) => {
        store.dispatch(InventoryService.refreshGeneralInventoryLot(lot));
    };

    const onInventoryRefresh = (inventory) => {
        store.dispatch(InventoryService.refreshGeneralInventory(inventory));
    };

    const onInventoryLotItemReading = (lotItem) => {
        // eslint-disable-next-line no-console
        store.dispatch(InventoryService.refreshInventoryLotItemReading(lotItem));
    };

    const onInventoryLockLot = (inventoryId, inventoryLotId, currentUser) => {
        // eslint-disable-next-line no-console
        console.info('****** NOTIFICATION INVENTORY LOCK LOT ******', inventoryId, inventoryLotId, currentUser);
    };

    const onInventoryUnlockLot = (inventoryId, inventoryLotId) => {
        // eslint-disable-next-line no-console
        console.info('****** NOTIFICATION INVENTORY UNLOCK LOT ******', inventoryId, inventoryLotId);
    };

    /*=======================================================================================
    *   Shipment Tracking
    =======================================================================================*/
    const onPushNotificationShipmentTrancking = (notification) => {
        // eslint-disable-next-line no-console
        console.info('****** PUSH NOTIFICATION NOTIFICATION SHIPMENT TRACKING ******', notification);
        sendPushNotificationShipmentTracking(notification);
    };


    /*=======================================================================================
    *   Connection
    =======================================================================================*/
    const createConnection = async () => {
        const protocol = new JsonHubProtocol();
        // let transport to fall back to to LongPolling if it needs to
        const transport = HttpTransportType.WebSockets || HttpTransportType.LongPolling;
        const options = {
            transport,
            logMessageContent: true,
            logger: LogLevel.Information,
            accessTokenFactory: () => action.user.token
        };

        const connectionHub = `${getUrlMessageApiWebSocket(window)}/hub`;
        // create the connection instance
        hub = new HubConnectionBuilder()
            .withUrl(connectionHub, options)
            .withHubProtocol(protocol)
            .withAutomaticReconnect()
            .build();

        hub.onreconnecting(error => {
            console.assert(hub.state === HubConnectionState.Reconnecting);

            console.info(`SignalR - Logistics Message: Connection lost due to error "${error}". Reconnecting.`);
        });

        hub.onreconnected(connectionId => {
            console.assert(hub.state === HubConnectionState.Connected);
            console.info(`SignalR - Logistics Message: Reconnected.`);
        });

        hub.onclose((error) => {
            console.assert(hub.state === HubConnectionState.Disconnected);
            console.info(`Connection closed due "${error}". Try refreshing this page to restart the connection.`);
        });

        /*=======================================================================================
        *   Commom
        =======================================================================================*/
        // event handlers, you can use these to dispatch actions to update your Redux store
        hub.on('Message', onMessageResponseReceived);
        hub.on('PushNotification', onPushNotification);

        /*=======================================================================================
        *   Expedition Picking
        =======================================================================================*/
        hub.on('ExpeditionPicking', onPickingResponseReceived);
        hub.on('ExpeditionRePicking', onRePickingResponseReceived);
        hub.on('ExpeditionPickingRefreshOrder', onPickingRefreshOrderResponseReceived);
        hub.on('ExpeditionRefreshOrderStatus', onPickingRefreshOrderStatusResponseReceived);
        hub.on('ExpeditionPickingLockOrder', onPickingLockOrderResponseReceived);
        hub.on('ExpeditionPickingUnlockOrder', onPickingUnlockOrderResponseReceived);
        hub.on('SendLinePickingCancelToGroup', onLinePickingCancelOrder);
        hub.on('SendLinePickingRefreshOrderToGroup', onLinePickingRefreshOrderResponseReceived);
        hub.on('SendPickingRefreshOrderResponseToUser', onLinePickingRefreshOrderResponseReceived);
        hub.on('SendLinePickingInsertOrderToGroup', onLinePickingAddOrder);
        /*=======================================================================================
        *   Inventory
        =======================================================================================*/
        hub.on('InventoryRefreshStatus', onInventoryRefreshStatus);
        hub.on('InventoryRefreshLot', onInventoryRefreshLot);
        hub.on('InventoryLotItemReading', onInventoryLotItemReading);
        hub.on('InventoryLockLot', onInventoryLockLot);
        hub.on('InventoryUnlockLot', onInventoryUnlockLot);
        hub.on('InventoryRefresh', onInventoryRefresh);

        /*=======================================================================================
        *   Shipment Tracking
        =======================================================================================*/
        hub.on('PN-ShipmentTracking', onPushNotificationShipmentTrancking);

        await startSignalRConnection();
    }
    // register signalR after the user logged in
    if (action && action.type === 'SET_USER') {
        if (!isNullOrEmpty(hub) && vision !== action.user.vision) {
            hub.stop().then(async () => await createConnection());
            //hub = null; 
        } else if (isNullOrEmpty(hub)) {
            createConnection();
        }

    }
    next(action);
};

export default signalRMiddleware;
